/* eslint-disable max-len */
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import React, { FC, useCallback, useState } from 'react'
import { useRouter } from 'next/router'
import Image from 'next/image'
import { Swiper, SwiperSlide } from 'swiper/react'
import { default as SwiperClass } from 'swiper/types/swiper-class'
import { MyProfileSpecialist } from '../profile-specialist.types'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import { TranslationDictionary, useLocale } from '@plandi/common'
import { ReactComponent as Like } from 'public/assets/Like.svg'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import { useProfileSpecialistPortfolioNewStyles } from './profile-specialist-portfolio-new.styles'
import { ProfileSpecialistPortfolioEditNew } from './profile-specialist-portfolio-edit-new'
import { useQueryProfilePortfolioProjectList } from 'src/features/portfolio/common/portfolio.query'
import { components } from '@plandi/common/src/generated'
import clsx from 'clsx'
import { RoundedArrowIcon } from 'src/jsx-ico'

interface IProfileSpecialistPortfolioNewProps {
  profile: MyProfileSpecialist
  locale: string
  translate?: TranslationDictionary['data']
  type: 'freelancer' | 'worker'
  hideEdit?: boolean
}

export const ProfileSpecialistPortfolioNew: FC<
  IProfileSpecialistPortfolioNewProps
> = ({ profile, translate, type, hideEdit }) => {
  const [showEdit, setShowEdit] = useState(false)
  const [indexPortfolio, setIndexPortfolio] = useState<number | null>(null)
  const locale = useLocale()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down(900))

  const router = useRouter()

  const classes = useProfileSpecialistPortfolioNewStyles()

  const { data: projects } = useQueryProfilePortfolioProjectList(profile?.id, {
    enabled: Boolean(profile?.id)
  })

  const handleCreateProject = () => {
    router.push(
      {
        pathname: `/profile-specialist/${profile?.slug_name}/${profile?.id}/portfolio/create`
      },
      undefined,
      { shallow: true }
    )
  }

  const [swiperInstace, setSwiperInstance] = useState<null | SwiperClass>(null)

  const handleNextSlide = useCallback(() => {
    if (!swiperInstace) return
    swiperInstace.slideNext()
  }, [swiperInstace])

  const handlePrevSlide = useCallback(() => {
    if (!swiperInstace) return
    swiperInstace.slidePrev()
  }, [swiperInstace])

  const portfolioCard = (
    project: components['schemas']['PortfolioProjectResource'],
    index: number
  ) => {
    return (
      <Box
        key={project.id}
        className={classes.portfolio}
        onMouseEnter={() => setIndexPortfolio(index)}
        onMouseLeave={() => setIndexPortfolio(null)}
        onClick={() =>
          router.push(
            {
              pathname: `/profile-specialist/${profile?.slug_name}/${profile?.id}/portfolio/${project.slug}`
            },
            undefined,
            { shallow: true }
          )
        }
      >
        <Image
          src={project?.coverUrl ?? ''}
          width={isMobile ? 281 : 253}
          height={isMobile ? 194 : 175}
          alt={project?.name}
          className={classes.portfolioImage}
          objectFit="cover"
          priority
        />
        <Typography
          className={classes.portfolioName}
          style={{
            color: indexPortfolio === index ? '#E52428' : 'inherit'
          }}
        >
          {project?.name}
        </Typography>
        <div className={classes.portfolioLikes}>
          <Like />
          <Typography className={classes.portfolioLike}>
            {project?.likes}
          </Typography>
        </div>
      </Box>
    )
  }
  if (hideEdit && projects?.length < 1) {
    return null
  }
  return (
    <div className={classes.root}>
      {showEdit ? (
        <ProfileSpecialistPortfolioEditNew
          projects={projects}
          profile={profile}
          setShowEdit={setShowEdit}
          translate={translate}
          locale={locale}
          type={type}
        />
      ) : (
        <div className={classes.bottom}>
          <div
            className={classes.headerBlock}
            style={{ marginBottom: isMobile && !projects?.length ? 0 : 4 }}
          >
            <Typography className={classes.header}>
              {locale === 'ru' ? 'Портфолио' : 'Portfolio'}
            </Typography>
            {!hideEdit && (
              <>
                {projects?.length ? (
                  isMobile ? (
                    <EditOutlinedIcon
                      color="secondary"
                      onClick={() => setShowEdit(true)}
                    />
                  ) : (
                    <div className={classes.bottomEdit}>
                      <EditOutlinedIcon color="inherit" />
                      <Typography
                        className={classes.edit}
                        onClick={() => setShowEdit(true)}
                      >
                        {locale === 'ru' ? 'Редактировать' : 'Edit'}
                      </Typography>
                    </div>
                  )
                ) : isMobile ? (
                  <AddCircleOutlineIcon
                    style={{ color: '#E52428' }}
                    onClick={handleCreateProject}
                  />
                ) : (
                  <div className={classes.bottomEdit}>
                    <AddCircleOutlineIcon
                      style={{ color: '#E52428' }}
                      onClick={handleCreateProject}
                    />
                    <Typography
                      className={classes.edit}
                      onClick={handleCreateProject}
                    >
                      {locale === 'ru' ? 'Создать проект' : 'Creat Project'}
                    </Typography>
                  </div>
                )}
              </>
            )}
          </div>
          {projects?.length ? (
            isMobile ? (
              <Swiper
                slidesPerView={1}
                spaceBetween={16}
                onSwiper={(swiper) => setSwiperInstance(swiper)}
              >
                {projects?.map((project, index) => (
                  <SwiperSlide
                    key={project?.id}
                    className={classes.cardWrapper}
                  >
                    {portfolioCard(project, index)}
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <div className={classes.portfolioBlock}>
                {projects?.map((project, index) =>
                  portfolioCard(project, index)
                )}
              </div>
            )
          ) : null}
          {isMobile && projects?.length ? (
            <div className={classes.controlButtonContainer}>
              <IconButton
                onClick={handlePrevSlide}
                className={clsx(classes.prevButton, classes.controlButton)}
                aria-label={'sliderControl'}
                disabled={projects?.length === 1}
              >
                <RoundedArrowIcon width={36} height={36} />
              </IconButton>
              <IconButton
                onClick={handleNextSlide}
                className={classes.controlButton}
                aria-label={'sliderControl'}
                disabled={projects?.length === 1}
              >
                <RoundedArrowIcon width={36} height={36} />
              </IconButton>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
